import type { Subject, SubjectCategory } from "@/types/courses";

export const SUBJECT_CATEGORIES: SubjectCategory[] = [
  {
    id: "engineering",
    label: "Engineering",
    shortLabel: "Engineering",
    description: "Core engineering disciplines, systems work, and applied design.",
  },
  {
    id: "science",
    label: "Science",
    shortLabel: "Science",
    description: "Mathematics, computing, physical sciences, and research-heavy subjects.",
  },
  {
    id: "hass",
    label: "Humanities, Arts, and Social Sciences",
    shortLabel: "HASS",
    description: "Human-centered study across arts, writing, social sciences, and culture.",
  },
  {
    id: "management",
    label: "Management",
    shortLabel: "Management",
    description: "Business, leadership, and organizational decision-making.",
  },
  {
    id: "architecture",
    label: "Architecture",
    shortLabel: "Architecture",
    description: "Built environments, design studios, and spatial systems.",
  },
  {
    id: "itws",
    label: "Information Technology and Web Science",
    shortLabel: "ITWS",
    description: "Digital systems, web platforms, and product-minded computing.",
  },
  {
    id: "interdisciplinary",
    label: "Interdisciplinary and Other",
    shortLabel: "Other",
    description: "ROTC, administrative, and cross-school subjects that do not fit one division.",
  },
  {
    id: "uncategorized",
    label: "Uncategorized",
    shortLabel: "Misc",
    description: "Subjects that still need a stronger academic grouping.",
  },
];

const HASS = "School of Humanities, Arts, and Social Sciences";
const ENGR = "School of Engineering";
const SCI = "School of Science";

export const MOCK_SUBJECTS: Subject[] = [
  {
    id: "arts",
    code: "ARTS",
    name: "Arts",
    category: "hass",
    school: HASS,
    description: "Studio, visual culture, and creative practice courses across mediums.",
    aliases: ["art", "visual arts", "creative"],
    popularity: 54,
  },
  {
    id: "cogs",
    code: "COGS",
    name: "Cognitive Science",
    category: "hass",
    school: HASS,
    description: "Mind, behavior, language, and interdisciplinary cognition studies.",
    aliases: ["brain", "cognition", "mind"],
    popularity: 72,
  },
  {
    id: "comm",
    code: "COMM",
    name: "Communication",
    category: "hass",
    school: HASS,
    description: "Writing, speaking, media, and communication strategy.",
    aliases: ["communications", "media", "public speaking"],
    popularity: 61,
  },
  {
    id: "econ",
    code: "ECON",
    name: "Economics",
    category: "hass",
    school: HASS,
    description: "Markets, policy, incentives, and quantitative social science.",
    aliases: ["economy", "finance", "policy"],
    popularity: 78,
    featured: true,
  },
  {
    id: "gsas",
    code: "GSAS",
    name: "Games and Simulation Arts and Sciences",
    category: "hass",
    school: HASS,
    description: "Game design, interactive media, and simulation storytelling.",
    aliases: ["games", "simulation", "game design"],
    popularity: 93,
    featured: true,
  },
  {
    id: "ihss",
    code: "IHSS",
    name: "Interdisciplinary Humanities and Social Sciences",
    category: "hass",
    school: HASS,
    description: "Interdisciplinary HASS seminars spanning technology, culture, and society.",
    aliases: ["humanities", "social sciences", "interdisciplinary hass"],
    popularity: 66,
  },
  {
    id: "inqr",
    code: "INQR",
    name: "HASS Inquiry",
    category: "hass",
    school: HASS,
    description: "Inquiry-based seminars that fulfill HASS exploration requirements.",
    aliases: ["inquiry", "hass inquiry", "seminar"],
    popularity: 57,
  },
  {
    id: "lang",
    code: "LANG",
    name: "Foreign Languages",
    category: "hass",
    school: HASS,
    description: "Language study with cultural and communication emphasis.",
    aliases: ["language", "spanish", "french", "chinese"],
    popularity: 48,
  },
  {
    id: "litr",
    code: "LITR",
    name: "Literature",
    category: "hass",
    school: HASS,
    description: "Texts, criticism, interpretation, and literary history.",
    aliases: ["literary", "books", "reading"],
    popularity: 43,
  },
  {
    id: "phil",
    code: "PHIL",
    name: "Philosophy",
    category: "hass",
    school: HASS,
    description: "Ethics, logic, metaphysics, and philosophical inquiry.",
    aliases: ["ethics", "logic", "reasoning"],
    popularity: 64,
  },
  {
    id: "psyc",
    code: "PSYC",
    name: "Psychology",
    category: "hass",
    school: HASS,
    description: "Behavior, cognition, and the science of human experience.",
    aliases: ["psych", "behavior", "mental"],
    popularity: 81,
    featured: true,
  },
  {
    id: "stso",
    code: "STSO",
    name: "Science, Technology, and Society",
    category: "hass",
    school: HASS,
    description: "The social impact, policy, and ethics of science and technology.",
    aliases: ["sts", "technology and society", "policy"],
    popularity: 69,
  },
  {
    id: "writ",
    code: "WRIT",
    name: "Writing",
    category: "hass",
    school: HASS,
    description: "Academic, professional, and persuasive writing courses.",
    aliases: ["composition", "writing studio", "rhetoric"],
    popularity: 59,
  },
  {
    id: "bmed",
    code: "BMED",
    name: "Biomedical Engineering",
    category: "engineering",
    school: ENGR,
    description: "Engineering at the intersection of medicine, devices, and biology.",
    aliases: ["biomed", "medical engineering", "bioengineering"],
    popularity: 76,
  },
  {
    id: "chme",
    code: "CHME",
    name: "Chemical Engineering",
    category: "engineering",
    school: ENGR,
    description: "Thermodynamics, process design, and chemical systems engineering.",
    aliases: ["chemical", "chem eng", "process"],
    popularity: 68,
  },
  {
    id: "civl",
    code: "CIVL",
    name: "Civil Engineering",
    category: "engineering",
    school: ENGR,
    description: "Infrastructure, structures, transportation, and the built environment.",
    aliases: ["civil", "structures", "infrastructure"],
    popularity: 62,
  },
  {
    id: "ecse",
    code: "ECSE",
    name: "Electrical, Computer, and Systems Engineering",
    category: "engineering",
    school: ENGR,
    description: "Circuits, embedded systems, signal processing, and computation.",
    aliases: ["ece", "electrical", "computer engineering", "systems"],
    popularity: 96,
    featured: true,
  },
  {
    id: "engr",
    code: "ENGR",
    name: "General Engineering",
    category: "engineering",
    school: ENGR,
    description: "Foundational engineering courses shared across disciplines.",
    aliases: ["intro engineering", "engineering core"],
    popularity: 58,
  },
  {
    id: "enve",
    code: "ENVE",
    name: "Environmental Engineering",
    category: "engineering",
    school: ENGR,
    description: "Water, sustainability, environmental systems, and public impact.",
    aliases: ["environmental", "sustainability", "water"],
    popularity: 52,
  },
  {
    id: "esci",
    code: "ESCI",
    name: "Engineering Science",
    category: "engineering",
    school: ENGR,
    description: "Cross-disciplinary engineering topics and scientific foundations.",
    aliases: ["engineering science", "applied science"],
    popularity: 45,
  },
  {
    id: "isye",
    code: "ISYE",
    name: "Industrial and Systems Engineering",
    category: "engineering",
    school: ENGR,
    description: "Operations, optimization, logistics, and systems design.",
    aliases: ["ise", "industrial", "systems engineering", "operations"],
    popularity: 74,
    featured: true,
  },
  {
    id: "mane",
    code: "MANE",
    name: "Mechanical, Aerospace, and Nuclear Engineering",
    category: "engineering",
    school: ENGR,
    description: "Mechanics, energy, aerospace systems, and advanced design.",
    aliases: ["mechanical", "aerospace", "nuclear", "meche"],
    popularity: 89,
    featured: true,
  },
  {
    id: "mtle",
    code: "MTLE",
    name: "Materials Science and Engineering",
    category: "engineering",
    school: ENGR,
    description: "Materials behavior, processing, and engineering performance.",
    aliases: ["materials", "mse", "materials engineering"],
    popularity: 50,
  },
  {
    id: "astr",
    code: "ASTR",
    name: "Astronomy",
    category: "science",
    school: SCI,
    description: "Planets, stars, and the physics of the universe.",
    aliases: ["space", "astrophysics", "cosmos"],
    popularity: 55,
  },
  {
    id: "bcbp",
    code: "BCBP",
    name: "Biochemistry and Biophysics",
    category: "science",
    school: SCI,
    description: "Molecular biology and the physics of living systems.",
    aliases: ["biochem", "biophysics", "molecular"],
    popularity: 49,
  },
  {
    id: "biol",
    code: "BIOL",
    name: "Biology",
    category: "science",
    school: SCI,
    description: "Organisms, cells, evolution, and biological systems.",
    aliases: ["life science", "bio"],
    popularity: 67,
  },
  {
    id: "chem",
    code: "CHEM",
    name: "Chemistry",
    category: "science",
    school: SCI,
    description: "Chemical structure, reactions, and laboratory science.",
    aliases: ["chem", "organic chemistry", "lab"],
    popularity: 71,
  },
  {
    id: "csci",
    code: "CSCI",
    name: "Computer Science",
    category: "science",
    school: SCI,
    description: "Programming, theory, systems, AI, and modern software foundations.",
    aliases: ["cs", "coding", "software", "computer science"],
    popularity: 100,
    featured: true,
  },
  {
    id: "erth",
    code: "ERTH",
    name: "Earth and Environmental Science",
    category: "science",
    school: SCI,
    description: "Earth systems, climate, geology, and environmental analysis.",
    aliases: ["earth science", "geology", "environmental science"],
    popularity: 47,
  },
  {
    id: "isci",
    code: "ISCI",
    name: "Interdisciplinary Science",
    category: "science",
    school: SCI,
    description: "Science topics that bridge multiple traditional disciplines.",
    aliases: ["interdisciplinary science", "science seminar"],
    popularity: 41,
  },
  {
    id: "math",
    code: "MATH",
    name: "Mathematics",
    category: "science",
    school: SCI,
    description: "Calculus, algebra, proofs, and theoretical foundations.",
    aliases: ["maths", "mathematical", "calculus", "algebra"],
    popularity: 95,
    featured: true,
  },
  {
    id: "matp",
    code: "MATP",
    name: "Mathematical Programming, Probability, and Statistics",
    category: "science",
    school: SCI,
    description: "Probability, statistics, and optimization for quantitative work.",
    aliases: ["statistics", "probability", "optimization", "applied math"],
    popularity: 79,
    featured: true,
  },
  {
    id: "phys",
    code: "PHYS",
    name: "Physics",
    category: "science",
    school: SCI,
    description: "Mechanics, electromagnetism, modern physics, and modeling.",
    aliases: ["physics", "mechanics", "quantum"],
    popularity: 77,
  },
  {
    id: "arch",
    code: "ARCH",
    name: "Architecture",
    category: "architecture",
    school: "School of Architecture",
    description: "Design studios, built form, representation, and spatial thinking.",
    aliases: ["architectural design", "studio"],
    popularity: 73,
    featured: true,
  },
  {
    id: "lght",
    code: "LGHT",
    name: "Lighting",
    category: "architecture",
    school: "School of Architecture",
    description: "Lighting design, perception, and architectural experience.",
    aliases: ["lighting design", "illumination"],
    popularity: 44,
  },
  {
    id: "itws",
    code: "ITWS",
    name: "Information Technology and Web Science",
    category: "itws",
    school: "School of Science",
    description: "Product, web, data, and full-stack systems for digital experiences.",
    aliases: ["web science", "it", "web", "full stack"],
    popularity: 88,
    featured: true,
  },
  {
    id: "busn",
    code: "BUSN",
    name: "Business (H)",
    category: "management",
    school: "Lally School of Management",
    description: "Broad business topics, strategy, and professional fundamentals.",
    aliases: ["business", "entrepreneurship"],
    popularity: 63,
  },
  {
    id: "mgmt",
    code: "MGMT",
    name: "Management",
    category: "management",
    school: "Lally School of Management",
    description: "Management, analytics, leadership, and organizational strategy.",
    aliases: ["management", "leadership", "operations"],
    popularity: 82,
    featured: true,
  },
  {
    id: "admn",
    code: "ADMN",
    name: "Administrative Courses",
    category: "interdisciplinary",
    school: "Institute-wide",
    description: "Administrative and non-standard course listings used across the institute.",
    aliases: ["admin", "administrative"],
    popularity: 16,
  },
  {
    id: "usaf",
    code: "USAF",
    name: "Aerospace Studies (Air Force ROTC)",
    category: "interdisciplinary",
    school: "Institute-wide",
    description: "Air Force ROTC coursework and leadership training.",
    aliases: ["air force", "rotc", "aerospace studies"],
    popularity: 22,
  },
  {
    id: "usar",
    code: "USAR",
    name: "Military Science (Army ROTC)",
    category: "interdisciplinary",
    school: "Institute-wide",
    description: "Army ROTC leadership, military science, and training courses.",
    aliases: ["army", "rotc", "military science"],
    popularity: 19,
  },
  {
    id: "usna",
    code: "USNA",
    name: "Naval Science (Navy ROTC)",
    category: "interdisciplinary",
    school: "Institute-wide",
    description: "Navy ROTC courses in naval science and officer development.",
    aliases: ["navy", "rotc", "naval science"],
    popularity: 20,
  },
  {
    id: "ilea",
    code: "ILEA",
    name: "Independent Learning Exper",
    category: "uncategorized",
    school: "Institute-wide",
    description: "Independent learning experiences and special academic formats.",
    aliases: ["independent study", "special topics", "independent learning"],
    popularity: 24,
  },
];
